<script lang="ts">
  // The preview of one file: a Markdown document rendered with its table of contents,
  // a text file as is, an image, a PDF; anything else can only be downloaded.
  import { api, ApiError, fileUrl, type FileItem } from '../lib/api';
  import { dayAndTime, formatSize } from '../lib/format';
  import { renderDocument, type Heading } from '../lib/markdown';
  import { messagePath, router } from '../lib/router.svelte';

  let { file, compact = false }: { file: FileItem; compact?: boolean } = $props();

  const MAX_TEXT = 1024 * 1024;
  const ext = $derived(file.name.split('.').pop()?.toLowerCase() ?? '');
  const kind = $derived.by(() => {
    if (file.mime === 'text/markdown' || ext === 'md' || ext === 'markdown') return 'markdown';
    if (file.mime === 'text/plain' || ext === 'txt' || file.mime === 'text/csv') return 'text';
    if (file.mime.startsWith('image/') && file.mime !== 'image/svg+xml') return 'image';
    if (file.mime === 'application/pdf') return 'pdf';
    return 'none';
  });

  let text = $state<string | null>(null);
  let doc = $state<{ html: string; toc: Heading[] } | null>(null);
  let error = $state('');
  let body: HTMLDivElement | undefined = $state();

  $effect(() => {
    const { id, size_bytes } = file;
    const k = kind;
    text = null;
    doc = null;
    error = '';
    if (k !== 'markdown' && k !== 'text') return;
    if (size_bytes > MAX_TEXT) {
      error = "Ce document dépasse 1 Mo : téléchargez-le pour le lire.";
      return;
    }
    let current = true;
    api
      .fileText(id)
      .then((content) => {
        if (!current) return;
        if (k === 'markdown') doc = renderDocument(content);
        else text = content;
      })
      .catch((e) => current && (error = e instanceof ApiError ? e.message : "Impossible d'afficher ce document"));
    return () => {
      current = false;
    };
  });

  function jump(id: string) {
    body?.querySelector(`#${CSS.escape(id)}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }
</script>

<article class="preview" class:compact aria-label="Aperçu de {file.name}">
  <header>
    <div class="title">
      <h2>{file.name}</h2>
      <p>{file.author.display_name} · {dayAndTime(file.created_at)} · {formatSize(file.size_bytes)}</p>
    </div>
    <div class="actions">
      <button type="button" class="btn" onclick={() => router.go(messagePath(file.conversation_id, file.message_id, file.root_id))}>
        Voir dans la discussion
      </button>
      <a class="btn primary" href={fileUrl(file.id)} download={file.name}>Télécharger</a>
    </div>
  </header>

  <div class="content" bind:this={body}>
    {#if error}
      <p class="status">{error}</p>
    {:else if kind === 'markdown'}
      {#if !doc}
        <p class="status">Chargement…</p>
      {:else}
        <div class="doc-layout">
          {#if doc.toc.length > 1}
            <nav class="toc" aria-label="Sommaire">
              <p class="toc-title">Sommaire</p>
              <ul>
                {#each doc.toc as h (h.id)}
                  <li class="level-{h.level}"><a href="#{h.id}" onclick={(e) => { e.preventDefault(); jump(h.id); }}>{h.text}</a></li>
                {/each}
              </ul>
            </nav>
          {/if}
          <!-- markdown-it without raw HTML (html: false). -->
          <div class="doc">{@html doc.html}</div>
        </div>
      {/if}
    {:else if kind === 'text'}
      {#if text === null}<p class="status">Chargement…</p>{:else}<pre class="plain">{text}</pre>{/if}
    {:else if kind === 'image'}
      <img class="image" src={fileUrl(file.id)} alt={file.name} />
    {:else if kind === 'pdf'}
      <iframe class="pdf" src={fileUrl(file.id)} title={file.name}></iframe>
    {:else}
      <p class="status">Pas d'aperçu pour ce type de fichier : téléchargez-le pour l'ouvrir.</p>
    {/if}
  </div>
</article>

<style>
  .preview {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: var(--bg);
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 20px;
    border-bottom: 1px solid var(--border);
  }
  .compact header {
    padding: 12px 16px;
  }
  .title {
    flex: 1;
    min-width: 180px;
  }
  h2 {
    margin: 0;
    font-size: 16px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }
  .title p {
    margin: 0;
    font-size: 12px;
    color: var(--muted);
  }
  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .actions .btn {
    height: 32px;
    font-size: 13px;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
  }
  .content {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 20px 24px;
  }
  .compact .content {
    padding: 16px;
  }
  .status {
    color: var(--muted);
    margin: 0;
  }
  .doc-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
  }
  .compact .doc-layout {
    flex-direction: column;
    gap: 12px;
  }
  .toc {
    flex: 0 0 200px;
    position: sticky;
    top: 0;
    font-size: 13px;
    padding: 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    background: var(--bg-page);
  }
  .compact .toc {
    position: static;
    flex: none;
    width: 100%;
    box-sizing: border-box;
  }
  .toc-title {
    margin: 0 0 6px;
    font-weight: 600;
    color: var(--muted);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .toc ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .toc .level-2 {
    padding-left: 10px;
  }
  .toc .level-3 {
    padding-left: 20px;
  }
  .toc a {
    color: var(--accent-text);
    text-decoration: none;
  }
  .toc a:hover {
    text-decoration: underline;
  }
  .doc {
    flex: 1;
    min-width: 0;
    max-width: var(--reading-max);
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
  .doc :global(h1),
  .doc :global(h2),
  .doc :global(h3) {
    line-height: 1.3;
    margin: 1.2em 0 0.4em;
    scroll-margin-top: 8px;
  }
  .doc :global(h1:first-child),
  .doc :global(h2:first-child) {
    margin-top: 0;
  }
  .doc :global(h1) {
    font-size: 22px;
  }
  .doc :global(h2) {
    font-size: 18px;
  }
  .doc :global(h3) {
    font-size: 16px;
  }
  .doc :global(code) {
    font-family: var(--font-mono);
    font-size: 13px;
    background: var(--bg-page);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0 4px;
  }
  .doc :global(pre) {
    padding: 12px 14px;
    background: #1a1d21;
    color: #e8eaed;
    border-radius: var(--radius-m);
    overflow-x: auto;
  }
  .doc :global(pre code) {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
  }
  .doc :global(blockquote) {
    margin: 0.6em 0;
    padding-left: 12px;
    border-left: 3px solid var(--border-strong);
    color: var(--ink-2);
  }
  .doc :global(table) {
    border-collapse: collapse;
  }
  .doc :global(th),
  .doc :global(td) {
    border: 1px solid var(--border);
    padding: 4px 8px;
  }
  .doc :global(img) {
    max-width: 100%;
  }
  .plain {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 13px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .image {
    display: block;
    max-width: 100%;
    max-height: 100%;
    margin: 0 auto;
  }
  .pdf {
    width: 100%;
    height: 100%;
    min-height: 480px;
    border: 0;
  }
</style>
